import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { IMG } from "@/constants/bbAssets";
import { scrollToId } from "@/lib/scroll";
import { SunMark, WaveLine } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ySub = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-24 md:pt-32 pb-14 md:pb-20">
      {/* faint sun behind the copy */}
      <SunMark className="absolute -top-6 -left-16 w-64 h-44 text-sun/15 rotate-[-8deg] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        {/* copy side */}
        <div className="lg:col-span-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-deep/80 border border-deep/15 rounded-full px-4 py-2 bg-white/50"
          >
            <span className="w-2 h-2 rounded-full bg-sun" />
            Tampa, Florida • Summer 2027
          </motion.p>

          <h1 className="hero-display mt-6 font-display uppercase text-deep leading-[1] text-[clamp(3.4rem,10.5vw,7rem)]">
            <MaskedLine delay={0.2}>Take Your</MaskedLine>
            <MaskedLine delay={0.32}>Game to</MaskedLine>
            <MaskedLine delay={0.44}>
              <span className="relative inline-block">
                The Sand<span className="text-sun">.</span>
                <WaveLine className="absolute -bottom-3 md:-bottom-5 left-0 w-full h-4 md:h-6 text-sun" />
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 max-w-md text-base md:text-lg text-deep-soft leading-relaxed"
          >
            A flexible way for volleyball players to learn the beach game, compete, and have
            fun in the sand.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.82 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-join"
              onClick={() => scrollToId("join")}
              className="group inline-flex items-center gap-2 bg-deep text-cream font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] px-7 py-4 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200"
            >
              Join the Interest List
              <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-ways"
              onClick={() => scrollToId("ways")}
              className="inline-flex items-center gap-2 border border-deep/25 text-deep font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] px-7 py-4 rounded-full hover:border-deep hover:bg-deep hover:text-cream transition-colors duration-200"
            >
              Ways to Play
              <ArrowDown className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        {/* editorial collage side */}
        <div className="lg:col-span-6 relative h-[420px] sm:h-[520px] lg:h-[620px]">
          <motion.div style={{ y: yMain }} className="absolute right-0 top-4 md:top-8 w-[82%] sm:w-[76%]">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 3 }}
              animate={{ opacity: 1, y: 0, rotate: 2 }}
              transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
              className="photo-frame rotate-2"
            >
              <img
                src={IMG.celebrate}
                alt="Beach volleyball teammates celebrating in the sand in Tampa"
                className="w-full aspect-[4/5] object-cover"
                data-testid="hero-photo-main"
              />
              <p className="pt-2.5 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-deep/70">
                The beach game — Tampa, FL
              </p>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: ySub }} className="absolute left-0 bottom-0 w-[54%] sm:w-[48%] z-10">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -8 }}
              animate={{ opacity: 1, y: 0, rotate: -5 }}
              transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
              className="photo-frame relative"
            >
              <span className="tape -top-3 left-1/2 -translate-x-1/2 rotate-[-4deg]" />
              <img
                src={IMG.spike}
                alt="Beach volleyball athlete attacking at the net in Tampa"
                className="w-full aspect-[3/4] object-cover"
                data-testid="hero-photo-secondary"
              />
              <p className="pt-2.5 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-deep/70">
                Good vibes only
              </p>
            </motion.div>
          </motion.div>

          {/* rotating sticker */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.9, ease: EASE }}
            className="absolute top-0 right-2 md:right-6 z-20"
          >
            <div className="animate-spin-slow w-24 h-24 md:w-28 md:h-28">
              <svg viewBox="0 0 120 120" className="w-full h-full">
                <defs>
                  <path id="bb-circle" d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0" />
                </defs>
                <circle cx="60" cy="60" r="58" fill="#F6A63A" />
                <text fill="#0B3144" fontSize="10.5" fontFamily="Space Mono, monospace" letterSpacing="0.75">
                  <textPath href="#bb-circle" textLength="282" lengthAdjust="spacingAndGlyphs">GOOD VIBES × VOLLEYBALL • SUMMER 2027 •</textPath>
                </text>
                <circle cx="60" cy="60" r="10" fill="#0B3144" />
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
